import { z } from 'zod';

export const booleanParameterSchema = z
  .object({
    always_required: z.boolean(),
    existingNames: z.array(z.string()).optional(),
    id: z.string(),
    type: z.enum(['boolean']),
    name: z
      .string()
      .min(1, { message: 'Name cannot be empty.' })
      .max(50, { message: 'Name cannot exceed 50 characters.' }),
    default: z.boolean().nullable(),
  })
  .refine((data) => !data.existingNames?.includes(data.name), {
    path: ['name'],
    message: 'A parameter with this name already exists.',
  });

export const durationParameterSchema = z
  .object({
    always_required: z.boolean(),
    existingNames: z.array(z.string()).optional(),
    id: z.string(),
    type: z.enum(['integer']),
    name: z
      .string()
      .min(1, { message: 'Name cannot be empty.' })
      .max(50, { message: 'Name cannot exceed 50 characters.' }),
    default: z.number().nullable(),
    minimum: z.number().nullable(),
    maximum: z.number().nullable(),
    multiple_of: z.number().nullable().optional(),
  })
  .refine((data) => !data.existingNames?.includes(data.name), {
    path: ['name'],
    message: 'A parameter with this name already exists.',
  })
  .refine((data) => !data.always_required || data.default !== 0, {
    path: ['default'],
    message: 'Field is required.',
  });

export const numberParameterSchema = z
  .object({
    always_required: z.boolean(),
    existingNames: z.array(z.string()).optional(),
    id: z.string(),
    type: z.enum(['float', 'integer']),
    name: z
      .string()
      .min(1, { message: 'Name cannot be empty.' })
      .max(50, { message: 'Name cannot exceed 50 characters.' }),
    default: z.number().nullable(),
    minimum: z.number().nullable(),
    maximum: z.number().nullable(),
    multiple_of: z.number().nullable().optional(),
  })
  .refine((data) => !data.existingNames?.includes(data.name), {
    path: ['name'],
    message: 'A parameter with this name already exists.',
  })
  .refine((data) => !data.always_required || data.default !== null, {
    path: ['default'],
    message: 'Field is required.',
  })
  .refine(
    (data) =>
      data.minimum == null ||
      data.maximum == null ||
      data.minimum <= data.maximum,
    {
      path: ['minimum', 'maximum'],
      message: 'Max value should be equal or greater than min.',
    }
  )
  .refine(
    (data) =>
      data.default == null ||
      data.minimum == null ||
      data.default >= data.minimum,
    (data) => ({
      path: ['default'],
      message: `Default value should be equal or greater than min: ${data.minimum}.`,
    })
  )
  .refine(
    (data) =>
      data.default == null ||
      data.maximum == null ||
      data.default <= data.maximum,
    (data) => ({
      path: ['default'],
      message: `Default value should be equal or less than max: ${data.maximum}.`,
    })
  );

export const stringParameterSchema = z
  .object({
    always_required: z.boolean(),
    existingNames: z.array(z.string()).optional(),
    is_directory_path: z.boolean(),
    is_file_path: z.boolean().nullable(),
    id: z.string(),
    type: z.enum(['string']),
    name: z
      .string()
      .min(1, { message: 'Name cannot be empty.' })
      .max(50, { message: 'Name cannot exceed 50 characters.' }),
    default: z.string().nullable(),
  })
  .refine((data) => !data.existingNames?.includes(data.name), {
    path: ['name'],
    message: 'A parameter with this name already exists.',
  })
  .refine((data) => !data.always_required || data.default !== '', {
    path: ['default'],
    message: 'Field is required.',
  });

export const fileParameterSchema = z
  .object({
    existingNames: z.array(z.string()).optional(),
    id: z.string(),
    type: z.enum(['file_ref']),
    name: z
      .string()
      .min(1, { message: 'Name cannot be empty.' })
      .max(50, { message: 'Name cannot exceed 50 characters.' }),
    default: z.string().nullable(),
  })
  .refine((data) => !data.existingNames?.includes(data.name), {
    path: ['name'],
    message: 'A parameter with this name already exists.',
  })
  .refine((data) => data.default !== '', {
    path: ['default'],
    message: 'File name is required.',
  });
